import {
  FC,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { debounce } from "lodash";
import __ from "src/utils/Translations";
import { useParams } from "react-router-dom";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import { usePotentialParents } from "src/Pages/Company/OrganizationStructure/api/usePotentialParents";
import useFetchAreasData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchAreasData";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface IAutocompleteMainArea {
  companyId: string;
  structureId: string;
  areaId?: string;
  dataTestId?: string;
}

const AutocompleteMainArea: FC<IAutocompleteMainArea> = ({
  companyId,
  structureId,
  areaId,
  dataTestId,
}) => {
  const { control, getValues, watch } = useFormContext();
  const [search, setSearch] = useState("");

  const manager = getValues("manager");
  const validManager = manager?.id === "structure" ? undefined : manager?.id;
  const currentParentValue = watch("parent");
  const checkedManagerAsMember = watch("managerAsMember");

  const query = useMemo(
    () => (search ? `(name=like=*${search}*)` : null),
    [search]
  );

  const isCreatingNewArea = !areaId;

  const {
    data: areaData,
    fetchNextPage: areaFetchNextPage,
    hasNextPage: areaHasNextPage,
    isFetching: areaIsFetching,
    error: areasError,
  } = useFetchAreasData(companyId, structureId, search);

  const {
    data: potentialParentsData,
    fetchNextPage: potentialFetchNextPage,
    hasNextPage: potentialHasNextPage,
    isFetching: potentialIsFetching,
  } = usePotentialParents({
    companyId,
    structureId,
    areaId: areaId!,
    withManager: validManager,
    query,
    enabled: !isCreatingNewArea && !!areaId,
  });

  const { areaId: excludedValue } = useParams<{ areaId: string }>();
  const { data: structureData, isFetching: structureDataIsFetching } =
    useFetchStructureData(companyId, structureId);

  const logStatus = useLogStatus();

  if (areasError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "AutocompleteMainArea: fetchAreasData failed",
      statusDetails: {
        source: "API",
        operation: "fetchAreasData",
        status: (areasError as AxiosError)?.response?.status,
        url: (areasError as AxiosError)?.config?.url,
      },
    });
  }

  const { data, fetchNextPage, hasNextPage, isFetching } = useMemo(() => {
    if (isCreatingNewArea) {
      return {
        data: areaData,
        fetchNextPage: areaFetchNextPage,
        hasNextPage: areaHasNextPage,
        isFetching: areaIsFetching,
      };
    }
    return {
      data: potentialParentsData,
      fetchNextPage: potentialFetchNextPage,
      hasNextPage: potentialHasNextPage,
      isFetching: potentialIsFetching,
    };
  }, [
    isCreatingNewArea,
    areaData,
    areaFetchNextPage,
    areaHasNextPage,
    areaIsFetching,
    potentialParentsData,
    potentialFetchNextPage,
    potentialHasNextPage,
    potentialIsFetching,
  ]);

  const options = useMemo(() => {
    if (!structureData) return [];

    const baseOptions = [
      { id: null, name: structureData.name, canBeParent: true },
      ...(data?.pages?.flatMap((page) =>
        page.content.map((item: any) => ({
          id: item.id,
          name: item.name || item.label,
          canBeParent: item.canBeParent ?? true,
          givenEmployeeCanInheritManager: item.givenEmployeeCanInheritManager,
        }))
      ) || []),
    ].filter((option) => option.id !== excludedValue);

    if (currentParentValue && currentParentValue.id !== null) {
      const parentExistsInOptions = baseOptions.some(
        (opt) => opt.id === currentParentValue.id
      );

      if (!parentExistsInOptions) {
        return [
          baseOptions[0],
          {
            id: currentParentValue.id,
            name: currentParentValue.name || currentParentValue.label,
            canBeParent: true,
          },
          ...baseOptions.slice(1),
        ];
      }
    }

    return baseOptions;
  }, [data, excludedValue, structureData, currentParentValue]);

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    if (
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight > 0.9 &&
      hasNextPage &&
      !isFetching
    ) {
      fetchNextPage();
    }
  };

  if (structureDataIsFetching) {
    return <Skeleton width="100%" height={56} />;
  }

  return (
    <Controller
      name="parent"
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <Autocomplete
          options={options}
          getOptionDisabled={(option) =>
            option.canBeParent === false ||
            (option.givenEmployeeCanInheritManager === false &&
              checkedManagerAsMember)
          }
          disableListWrap
          disablePortal
          value={value || null}
          onChange={(_, newValue) => onChange(newValue)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason !== "reset") {
              debouncedSearch(event, newInputValue);
            }
          }}
          onOpen={() => setSearch("")}
          noOptionsText={__("Brak wyników wyszukiwania")}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return `${option}`;
            }

            const baseName =
              option?.name ?? option?.label ?? structureData?.name;

            if (!option.canBeParent) {
              return `${baseName} - ${__("Obszar podrzędny")}`;
            }
            if (
              option.givenEmployeeCanInheritManager === false &&
              checkedManagerAsMember
            ) {
              return `${baseName} - ${__("Konflikt z przełożonym")}`;
            }
            return baseName;
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testId={dataTestId}
              label={__("Nadrzędny obszar")}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps?.input,
                  endAdornment: (
                    <>
                      {isFetching && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {(params.slotProps?.input as any)?.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: handleScroll,
              style: { maxHeight: "300px", overflowY: "auto" },
            },
          }}
        />
      )}
    />
  );
};

export default AutocompleteMainArea;
